import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { agentsService } from '../services/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';

const REPORT_SECTIONS = [
  ['quadro_resumo', 'Quadro Resumo'],
  ['introducao', 'Introdução'],
  ['metodologia', 'Metodologia'],
  ['analise_documentos', 'Análise de Documentos'],
  ['analise_entrevistas', 'Análise de Entrevistas'],
  ['transcricoes_entrevistas', 'Transcrições de Entrevistas'],
  ['respostas_perguntas', 'Respostas às Perguntas'],
  ['conclusoes', 'Conclusões'],
  ['recomendacoes', 'Recomendações']
];

const ReportViewer = ({ complaintId, currentPhase, canEdit = true, onRefresh }) => {
  const [phase, setPhase] = useState(currentPhase || 'none');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setPhase(currentPhase || 'none');
  }, [currentPhase]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const response = await agentsService.getReport(complaintId);
        if (cancelled) {
          return;
        }

        setPhase(response.phase || currentPhase || 'none');
        setReport(response.relatorio_final || null);
        setError('');
      } catch (fetchError) {
        if (cancelled) {
          return;
        }

        setError(fetchError.response?.data?.error || fetchError.message);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [complaintId, currentPhase]);

  useEffect(() => {
    if (phase !== 'report_pending') {
      return undefined;
    }

    const interval = window.setInterval(async () => {
      try {
        const response = await agentsService.getReport(complaintId);
        setPhase(response.phase || 'none');
        setReport(response.relatorio_final || null);

        if (response.phase === 'report_done' || response.phase === 'report_approved') {
          toast.success('Relatório final gerado.');
          onRefresh?.();
        }
      } catch (pollError) {
        setError(pollError.response?.data?.error || pollError.message);
      }
    }, 2500);

    return () => window.clearInterval(interval);
  }, [phase, complaintId, onRefresh]);

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      setError('');
      await agentsService.triggerReport(complaintId);
      setPhase('report_pending');
      toast.info('Geração do relatório iniciada.');
      onRefresh?.();
    } catch (generateError) {
      setError(generateError.response?.data?.error || generateError.message);
      toast.error('Erro ao gerar relatório.');
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async (approved) => {
    try {
      setApprovalLoading(true);
      await agentsService.approveReport(complaintId, approved);
      setPhase(approved ? 'report_approved' : 'report_done');
      toast.success(approved ? 'Relatório aprovado.' : 'Relatório devolvido.');
      onRefresh?.();
    } catch (approveError) {
      setError(approveError.response?.data?.error || approveError.message);
      toast.error('Erro ao atualizar aprovação do relatório.');
    } finally {
      setApprovalLoading(false);
    }
  };

  const canGenerate = phase === 'approved' || phase === 'report_done' || phase === 'report_approved';
  const hasReport = phase === 'report_done' || phase === 'report_approved';

  return (
    <Card>
      <CardHeader className="bg-gray-50">
        <CardTitle>Relatório Final</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {loading ? <div className="text-sm text-gray-500">Carregando relatório...</div> : null}

        <div className="flex flex-wrap items-center gap-3">
          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium uppercase text-gray-700">
            {phase}
          </span>

          {phase === 'report_pending' ? (
            <span className="text-sm text-blue-700">Consolidando entrevistas e evidências...</span>
          ) : null}

          {canEdit && canGenerate ? (
            <Button onClick={handleGenerate} disabled={generating || approvalLoading || phase === 'report_pending'}>
              {hasReport ? 'Gerar Nova Versão' : 'Gerar Relatório'}
            </Button>
          ) : null}
        </div>

        {!canGenerate && phase !== 'report_pending' ? (
          <Alert variant="warning">
            <AlertDescription>
              O relatório só pode ser gerado depois que o plano estiver aprovado.
            </AlertDescription>
          </Alert>
        ) : null}

        {hasReport && report ? (
          <div className="space-y-4">
            {REPORT_SECTIONS.map(([field, label]) => (
              <div key={field} className="rounded-lg border border-gray-200 p-4">
                <h4 className="mb-2 font-semibold text-gray-900">{label}</h4>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                  {report[field] || 'Seção sem conteúdo.'}
                </p>
              </div>
            ))}

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
              <p className="font-medium text-gray-900">Resumo técnico</p>
              <p className="mt-1">
                Tokens: {report.metadata?.tokens_used || 0} | Custo: ${(report.metadata?.cost_usd || 0).toFixed(6)}
              </p>
            </div>

            {canEdit ? (
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => handleApprove(true)}
                  disabled={approvalLoading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Aprovar Relatório
                </Button>
                <Button
                  onClick={() => handleApprove(false)}
                  disabled={approvalLoading}
                  variant="outline"
                >
                  Devolver para Ajuste
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default ReportViewer;
